type SmoothTextStreamOptions = {
  /** Disable animation, primarily for reduced-motion preferences. */
  animate?: boolean;
  /** Reveal at this fixed rate instead of the backlog-proportional catch-up.
   *  Scripted replies use it: their whole text arrives in one push, and the
   *  catch-up pace would sprint through the bulk and crawl through the tail. */
  charsPerSecond?: number;
};

export type SmoothTextStream = {
  /** Add newly received text to the reveal queue. */
  push: (text: string) => void;
  /** Resolve once every queued character has been revealed. */
  finish: () => Promise<void>;
  /** Reveal everything immediately. Used when a stream is stopped or fails. */
  flush: () => void;
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/**
 * Turns irregular network chunks into a steady visual stream.
 *
 * Small queues advance one Unicode code point per animation frame. Larger
 * queues catch up in small batches, avoiding both a long delay after the model
 * finishes and a costly Markdown/React render for every individual character.
 */
export const createSmoothTextStream = (
  onUpdate: (text: string) => void,
  { animate = !prefersReducedMotion(), charsPerSecond }: SmoothTextStreamOptions = {},
): SmoothTextStream => {
  let visible = "";
  const queued: string[] = [];
  let cursor = 0;
  let frame: number | null = null;
  let finishPromise: Promise<void> | null = null;
  let resolveFinish: (() => void) | null = null;
  // Fixed-rate mode: the fractional character budget accrued but not yet
  // revealed, and the timestamp it was last topped up. Time-based rather than
  // frame-based, so a throttled tab catches up instead of stretching.
  let carry = 0;
  let lastTick: number | null = null;

  const remaining = () => queued.length - cursor;

  const completeIfDrained = () => {
    if (remaining() === 0 && resolveFinish) {
      resolveFinish();
      resolveFinish = null;
    }
  };

  const draw = (now: number) => {
    frame = null;
    const pending = remaining();
    if (pending === 0) {
      completeIfDrained();
      return;
    }

    if (charsPerSecond !== undefined) {
      const elapsed = lastTick === null ? 1000 / 60 : now - lastTick;
      lastTick = now;
      carry += (charsPerSecond * elapsed) / 1000;
    } else {
      // Usually this is one character. A large burst catches up over roughly a
      // few dozen frames instead of leaving the UI far behind the network.
      carry += Math.max(1, Math.ceil(pending / 24));
    }
    const count = Math.min(pending, Math.floor(carry));
    carry -= count;
    if (count > 0) {
      visible += queued.slice(cursor, cursor + count).join("");
      cursor += count;
      onUpdate(visible);
    }

    if (remaining() > 0) {
      frame = window.requestAnimationFrame(draw);
    } else {
      completeIfDrained();
    }
  };

  const schedule = () => {
    if (frame === null && remaining() > 0) {
      frame = window.requestAnimationFrame(draw);
    }
  };

  const flush = () => {
    if (frame !== null) {
      window.cancelAnimationFrame(frame);
      frame = null;
    }
    if (remaining() > 0) {
      visible += queued.slice(cursor).join("");
      cursor = queued.length;
      onUpdate(visible);
    }
    completeIfDrained();
  };

  return {
    push: text => {
      if (text === "") return;
      // Array.from splits by Unicode code point, so emoji are never displayed
      // as half of a UTF-16 surrogate pair.
      queued.push(...Array.from(text));
      if (animate) {
        schedule();
      } else {
        flush();
      }
    },
    finish: () => {
      if (remaining() === 0) return Promise.resolve();
      if (!finishPromise) {
        finishPromise = new Promise(resolve => {
          resolveFinish = resolve;
        });
      }
      schedule();
      return finishPromise;
    },
    flush,
  };
};
